import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    FileText,
    Headphones,
    ChevronLeft,
    ChevronRight,
    Loader2,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useNavigate } from 'react-router-dom';
import { getAppointments, AppointmentItem } from '../../api/Appointment';
import { getScheduledRange, isScheduledInRange, CALENDAR_VIEW_BY_PERIOD } from '../../utils/scheduledRange';
import AppointmentDetailPopover, {
    AppointmentDetailInfo,
    buildAppointmentDetailInfo,
    computePopoverPosition,
    hexToRgba,
} from '../../components/AppointmentDetailPopover';

dayjs.extend(isoWeek);

/**
 * API /appointments hiện chỉ lọc theo khoảng ngày TẠO (date_from/date_to), chưa có
 * tham số lọc theo `scheduled_at`. Vì vậy lịch tải toàn bộ lịch hẹn theo bộ lọc đang
 * chọn rồi tự nhóm theo ngày hẹn ở phía giao diện.
 */
const PAGE_SIZE = 200;
const MAX_PAGES = 10;
/** API không trả giờ kết thúc -> mỗi lịch hẹn hiển thị như một khối 60 phút */
const SLOT_MINUTES = 60;
/** Chế độ Ngày: độ rộng tối đa mỗi cột thẻ, tránh thẻ bị kéo dài hết màn hình rộng */
const DAY_CARD_MAX_WIDTH = 300;

const WEEK_DAYS = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'CN'];
const WEEK_DAYS_FULL = ['Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy', 'Chủ Nhật'];

export interface CalendarFilters {
    search?: string;
    status?: number;
    telesale_id?: number;
    staff_id?: number;
    branch_id?: number;
    /** 1 = lịch tư vấn, 2 = lịch thực hiện; bỏ trống = tất cả lịch hẹn */
    type_appointment?: number;
    /** Lọc nhân viên phụ trách (nhiều người) */
    staff_filter?: number[];
    /** Lọc nhiều trạng thái */
    statuses?: number[];
    /** Khoảng ngày TẠO lịch hẹn */
    date_from?: string;
    date_to?: string;
}

interface CalendarProps {
    filters?: CalendarFilters;
    /** Kỳ đang chọn ở bộ lọc "Theo ngày hẹn" của trang: yesterday | date | tomorrow | week | month | year */
    scheduledPeriod?: string;
    /** Luôn mở chi tiết theo trang này (vd: /consultation/detail) thay vì theo trạng thái của lịch hẹn */
    detailBasePath?: string;
}

/** Thông tin hiển thị trong popover + các trường phục vụ bố cục lịch */
interface CalendarEvent extends AppointmentDetailInfo {
    dateKey: string;
    minutes: number;
    cancelled: boolean;
    col?: number;
}

/* ================= HELPERS ================= */

const mapAppointment = (a: AppointmentItem): CalendarEvent | null => {
    if (!a?.scheduled_at) return null;
    const start = dayjs(String(a.scheduled_at).replace(' ', 'T'));
    if (!start.isValid()) return null;

    return {
        ...buildAppointmentDetailInfo(a),
        dateKey: start.format('YYYY-MM-DD'),
        minutes: start.hour() * 60 + start.minute(),
        cancelled: Number(a.status) === 0,
    };
};

/** Xếp các lịch trùng giờ thành nhiều cột song song */
const layoutColumns = (events: CalendarEvent[]) => {
    const colEnds: number[] = [];
    const placed = events.map(e => ({ ...e }));
    placed.forEach(e => {
        const end = e.minutes + SLOT_MINUTES;
        let done = false;
        for (let i = 0; i < colEnds.length; i++) {
            if (colEnds[i] <= e.minutes) {
                colEnds[i] = end;
                e.col = i;
                done = true;
                break;
            }
        }
        if (!done) {
            e.col = colEnds.length;
            colEnds.push(end);
        }
    });
    return { events: placed, numCols: colEnds.length || 1 };
};

/** Khung giờ hiển thị: mặc định 8h-18h, tự nới ra nếu có lịch nằm ngoài */
const getHourRange = (events: CalendarEvent[]) => {
    let min = 8;
    let max = 18;
    events.forEach(e => {
        const h = Math.floor(e.minutes / 60);
        if (h < min) min = h;
        if (h + 1 > max) max = h + 1;
    });
    if (max > 23) max = 23;
    return { min, max };
};

const minutesToPixels = (minutes: number, startHour: number, pxPerHour: number) =>
    ((minutes - startHour * 60) / 60) * pxPerHour;

/* ================= VIEWS ================= */

interface ViewProps {
    anchor: Dayjs;
    eventsByDate: Record<string, CalendarEvent[]>;
    today: Dayjs;
    onInteract: (e: React.MouseEvent, ev: CalendarEvent) => void;
    onHoverEnd: () => void;
}

const EventChip = ({ event, onInteract, onHoverEnd }: { event: CalendarEvent; onInteract: ViewProps['onInteract']; onHoverEnd: () => void }) => (
    <div
        onClick={(e) => onInteract(e, event)}
        onMouseEnter={(e) => onInteract(e, event)}
        onMouseLeave={onHoverEnd}
        className={`rounded p-1 sm:p-1.5 text-[10px] sm:text-xs shadow-sm cursor-pointer hover:shadow-md transition border-l-2 sm:border-l-4 ${event.cancelled ? 'opacity-60' : ''}`}
        style={{ backgroundColor: hexToRgba(event.tagColor, 0.12), borderLeftColor: event.tagColor }}
    >
        <div className="font-semibold" style={{ color: event.tagColor }}>{event.startLabel}</div>
        <div className={`text-gray-800 dark:text-gray-200 font-medium truncate ${event.cancelled ? 'line-through decoration-gray-500' : ''}`}>
            {event.name}
        </div>
        {event.service && (
            <div className="text-gray-500 dark:text-gray-400 truncate text-[9px] sm:text-[10px] mt-0.5 hidden sm:block">{event.service}</div>
        )}
    </div>
);

const MonthView = ({ anchor, eventsByDate, today, onInteract, onHoverEnd }: ViewProps) => {
    const cells = useMemo(() => {
        const start = anchor.startOf('month').startOf('isoWeek');
        const end = anchor.endOf('month').endOf('isoWeek');
        const list: Dayjs[] = [];
        let d = start;
        while (d.isBefore(end)) {
            list.push(d);
            d = d.add(1, 'day');
        }
        return list;
    }, [anchor]);

    const rows = Math.ceil(cells.length / 7);

    return (
        <div className="animate-fade-in block">
            <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-center">
                {WEEK_DAYS.map((day, idx) => (
                    <div key={day} className={`py-2 ${idx < 6 ? 'border-r border-gray-200 dark:border-gray-700' : 'text-red-500 dark:text-red-400'}`}>{day}</div>
                ))}
            </div>
            <div className="flex flex-col">
                {Array.from({ length: rows }).map((_, rowIdx) => (
                    <div key={rowIdx} className={`grid grid-cols-7 min-h-[120px] sm:min-h-[140px] ${rowIdx < rows - 1 ? 'border-b border-gray-100 dark:border-gray-800' : ''}`}>
                        {cells.slice(rowIdx * 7, (rowIdx + 1) * 7).map((day) => {
                            const key = day.format('YYYY-MM-DD');
                            const isCurrentMonth = day.month() === anchor.month();
                            const isToday = key === today.format('YYYY-MM-DD');
                            const dayEvents = isCurrentMonth ? (eventsByDate[key] || []) : [];

                            return (
                                <div
                                    key={key}
                                    className={`p-1 sm:p-2 border-r border-gray-100 dark:border-gray-800 flex flex-col gap-1 overflow-y-auto max-h-[160px] custom-scrollbar ${isToday ? 'bg-blue-50/50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-900'}`}
                                >
                                    <div className="flex items-center justify-between gap-1 mb-1 min-w-0">
                                        {dayEvents.length > 0 ? (
                                            <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full bg-[#3e91ab]/20 ring-1 ring-[#3e91ab]/30 dark:bg-[#3e91ab]/35 text-[#1f6275] dark:text-[#a5e0f0] text-[10px] sm:text-[11px] font-semibold truncate">
                                                Tổng: <b className="mx-0.5 text-[11px] sm:text-xs">{dayEvents.length}</b><span className="hidden sm:inline">lịch hẹn</span>
                                            </span>
                                        ) : <span />}
                                        {isToday ? (
                                            <span className="inline-flex items-center justify-center w-6 h-6 shrink-0 rounded-full bg-blue-500 text-white font-bold text-xs sm:text-sm">{day.date()}</span>
                                        ) : (
                                            <span className={`shrink-0 text-xs sm:text-sm ${isCurrentMonth ? 'text-gray-700 dark:text-gray-300 font-medium' : 'text-gray-400 dark:text-gray-600'}`}>{day.date()}</span>
                                        )}
                                    </div>
                                    {dayEvents.map(ev => (
                                        <EventChip key={ev.id} event={ev} onInteract={onInteract} onHoverEnd={onHoverEnd} />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

const WeekView = ({ anchor, eventsByDate, today, onInteract, onHoverEnd }: ViewProps) => {
    const pxPerHour = 60;
    const weekStart = anchor.startOf('isoWeek');
    const days = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')), [weekStart]);
    const weekEvents = useMemo(
        () => days.flatMap(d => eventsByDate[d.format('YYYY-MM-DD')] || []),
        [days, eventsByDate]
    );
    const { min: startHour, max: endHour } = getHourRange(weekEvents);
    const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);
    const nowMinutes = today.hour() * 60 + today.minute();
    const showNow = days.some(d => d.isSame(today, 'day'));

    return (
        <div className="overflow-x-auto animate-fade-in pb-4 custom-scrollbar">
            <div className="min-w-[800px] lg:min-w-full">
                <div className="grid grid-cols-[50px_repeat(7,1fr)] sm:grid-cols-[60px_repeat(7,1fr)] border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center sticky top-0 z-20 shadow-sm">
                    <div className="py-2 border-r border-gray-200 dark:border-gray-700 flex items-end justify-center pb-2 text-[9px] sm:text-[10px]">GMT+7</div>
                    {days.map((day, i) => {
                        const isToday = day.isSame(today, 'day');
                        return (
                            <div key={day.format('YYYY-MM-DD')} className={`py-2 ${i < 6 ? 'border-r border-gray-200 dark:border-gray-700' : ''}`}>
                                <span className={`uppercase text-[10px] sm:text-xs ${isToday ? 'text-blue-600 dark:text-blue-400' : (i === 6 ? 'text-red-500 dark:text-red-400' : '')}`}>{WEEK_DAYS[i]}</span><br />
                                <span className={`mt-0.5 sm:mt-1 inline-flex items-center justify-center ${isToday ? 'text-white w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-blue-500 shadow-sm font-bold' : 'text-gray-900 dark:text-gray-200 text-sm sm:text-base font-medium'}`}>{day.date()}</span>
                            </div>
                        );
                    })}
                </div>

                <div className="grid grid-cols-[50px_repeat(7,1fr)] sm:grid-cols-[60px_repeat(7,1fr)] bg-white dark:bg-gray-900 relative">
                    <div className="border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500 text-[10px] sm:text-[11px] font-medium relative text-right pr-1">
                        {hours.map(h => (
                            <div key={h} className="h-[60px] relative"><span className="absolute -top-2 right-1 bg-gray-50 dark:bg-gray-800 px-1 leading-none">{h.toString().padStart(2, '0')}:00</span></div>
                        ))}
                    </div>

                    {days.map((day, i) => {
                        const isToday = day.isSame(today, 'day');
                        const raw = (eventsByDate[day.format('YYYY-MM-DD')] || []);
                        const { events, numCols } = layoutColumns(raw);

                        return (
                            <div key={day.format('YYYY-MM-DD')} className={`relative group ${i < 6 ? 'border-r border-gray-100 dark:border-gray-800' : ''} ${isToday ? 'bg-blue-50/20 dark:bg-blue-900/10' : (i >= 5 ? 'bg-gray-50/30 dark:bg-gray-800/20' : '')}`}>
                                {hours.map(h => (
                                    <div key={h} className={`h-[60px] border-t border-gray-100 dark:border-gray-800 ${isToday ? 'border-dashed' : ''}`}></div>
                                ))}

                                {events.map(ev => {
                                    const topPx = minutesToPixels(ev.minutes, startHour, pxPerHour);
                                    const hPx = (SLOT_MINUTES / 60) * pxPerHour;
                                    const widthPercent = 100 / numCols;
                                    const leftPercent = (ev.col || 0) * widthPercent;

                                    return (
                                        <div
                                            key={ev.id}
                                            onClick={(e) => onInteract(e, ev)}
                                            onMouseEnter={(e) => onInteract(e, ev)}
                                            onMouseLeave={onHoverEnd}
                                            className={`absolute rounded shadow-sm overflow-hidden z-10 hover:shadow-md cursor-pointer transition-all border-l-2 sm:border-l-4 ${ev.cancelled ? 'opacity-60' : ''}`}
                                            style={{
                                                top: `${topPx}px`,
                                                height: `${hPx - 2}px`,
                                                left: `calc(${leftPercent}% + 1px)`,
                                                width: `calc(${widthPercent}% - 2px)`,
                                                backgroundColor: hexToRgba(ev.tagColor, 0.12),
                                                borderLeftColor: ev.tagColor,
                                            }}
                                        >
                                            <div className="px-1 py-0.5 h-full flex flex-col">
                                                <div className="text-[9px] sm:text-[10px] font-semibold leading-none mb-0.5 mt-0.5" style={{ color: ev.tagColor }}>{ev.startLabel}</div>
                                                <div className={`text-[10px] sm:text-[11px] text-gray-800 dark:text-gray-200 truncate font-medium ${ev.cancelled ? 'line-through' : ''} leading-tight`}>{ev.name}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}

                    {showNow && nowMinutes >= startHour * 60 && nowMinutes <= endHour * 60 && (
                        <div className="absolute right-0 h-px bg-red-400 z-20 pointer-events-none" style={{ top: `${minutesToPixels(nowMinutes, startHour, pxPerHour)}px`, left: '50px' }}>
                            <div className="absolute -left-1.5 -top-1 w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm"></div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const DayView = ({ anchor, eventsByDate, today, onInteract, onHoverEnd }: ViewProps) => {
    const pxPerHour = 100;
    const key = anchor.format('YYYY-MM-DD');
    const raw = eventsByDate[key] || [];
    const { min: startHour, max: endHour } = getHourRange(raw);
    const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);
    const { events, numCols } = layoutColumns(raw);
    const isToday = anchor.isSame(today, 'day');
    const nowMinutes = today.hour() * 60 + today.minute();

    return (
        <div className="overflow-x-auto animate-fade-in pb-4 custom-scrollbar">
            <div className="min-w-[600px] lg:min-w-full">
                <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3 sm:p-4 sticky top-0 z-20 shadow-sm">
                    <div className="w-12 sm:w-16"></div>
                    <div className="flex-1 flex flex-col justify-center">
                        <div className="text-xs sm:text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase">
                            {isToday ? 'Hôm nay, ' : ''}{WEEK_DAYS_FULL[anchor.isoWeekday() - 1]}
                        </div>
                        <div className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-baseline gap-2">
                            {anchor.date()} <span className="text-sm sm:text-lg font-medium text-gray-500 dark:text-gray-400">Tháng {anchor.month() + 1}, {anchor.year()}</span>
                        </div>
                    </div>
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">{raw.length} lịch hẹn</div>
                </div>
                <div className="flex relative bg-white dark:bg-gray-900 pb-6">
                    <div className="w-12 sm:w-16 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500 text-[10px] sm:text-xs font-medium relative">
                        {hours.map(h => (
                            <div key={h} className="h-[100px] relative"><span className="absolute -top-2 right-1 sm:right-2 bg-gray-50 dark:bg-gray-800 px-1 leading-none">{h.toString().padStart(2, '0')}:00</span></div>
                        ))}
                    </div>

                    <div className="flex-1 relative">
                        {hours.map(h => (
                            <div key={h} className="h-[100px] border-t border-gray-100 dark:border-gray-800 border-dashed"></div>
                        ))}

                        {isToday && nowMinutes >= startHour * 60 && nowMinutes <= endHour * 60 && (
                            <div className="absolute left-0 right-0 h-[2px] bg-red-500 z-20 pointer-events-none" style={{ top: `${minutesToPixels(nowMinutes, startHour, pxPerHour)}px` }}>
                                <div className="absolute -left-1.5 sm:-left-2 -top-[4px] sm:-top-[5px] w-2.5 sm:w-3 h-2.5 sm:h-3 rounded-full bg-red-500 shadow"></div>
                            </div>
                        )}

                        {events.map(ev => {
                            const topPx = minutesToPixels(ev.minutes, startHour, pxPerHour);
                            const hPx = (SLOT_MINUTES / 60) * pxPerHour;
                            // Mỗi cột rộng tối đa DAY_CARD_MAX_WIDTH; màn hẹp thì chia đều như cũ
                            const colWidth = `min(${100 / numCols}%, ${DAY_CARD_MAX_WIDTH}px)`;

                            return (
                                <div
                                    key={ev.id}
                                    onClick={(e) => onInteract(e, ev)}
                                    onMouseEnter={(e) => onInteract(e, ev)}
                                    onMouseLeave={onHoverEnd}
                                    className={`absolute rounded-md px-2.5 py-2 shadow-sm hover:shadow-md transition cursor-pointer z-10 border-l-4 overflow-hidden flex flex-col gap-0.5 ${ev.cancelled ? 'opacity-60' : ''}`}
                                    style={{
                                        top: `${topPx}px`,
                                        height: `${hPx - 4}px`,
                                        left: `calc(${ev.col || 0} * ${colWidth} + 4px)`,
                                        width: `calc(${colWidth} - 8px)`,
                                        backgroundColor: hexToRgba(ev.tagColor, 0.12),
                                        borderLeftColor: ev.tagColor,
                                    }}
                                >
                                    <div className="flex justify-between items-center gap-2 min-w-0">
                                        <div className="font-bold text-xs sm:text-sm shrink-0" style={{ color: ev.tagColor }}>{ev.startLabel}</div>
                                        {ev.tagName && (
                                            <span
                                                className="px-1.5 py-0.5 rounded text-[10px] sm:text-[11px] font-semibold truncate min-w-0"
                                                style={{ backgroundColor: hexToRgba(ev.tagColor, 0.18), color: ev.tagColor }}
                                                title={ev.tagName}
                                            >
                                                {ev.tagName}
                                            </span>
                                        )}
                                    </div>
                                    <div className={`text-gray-900 dark:text-gray-100 font-semibold text-sm truncate ${ev.cancelled ? 'line-through decoration-gray-500 text-gray-500' : ''}`} title={ev.name}>{ev.name}</div>
                                    {ev.telesale && (
                                        <div className="text-gray-600 dark:text-gray-400 text-[11px] flex items-center min-w-0 opacity-80"><Headphones className="w-3 h-3 mr-1 text-gray-400 shrink-0" /><span className="truncate">{ev.telesale}</span></div>
                                    )}
                                    {ev.service && (
                                        <div className="text-gray-600 dark:text-gray-400 text-[11px] flex items-center min-w-0 opacity-80"><FileText className="w-3 h-3 mr-1 text-gray-400 shrink-0" /><span className="truncate">{ev.service}</span></div>
                                    )}
                                </div>
                            );
                        })}

                        {!raw.length && (
                            <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500 pointer-events-none">
                                Không có lịch hẹn trong ngày này
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ================= MAIN ================= */

export default function AppointmentCalendar({ filters, scheduledPeriod, detailBasePath }: CalendarProps) {
    const navigate = useNavigate();
    const [view, setView] = useState<'month' | 'week' | 'day'>('month');
    const [anchor, setAnchor] = useState<Dayjs>(() => dayjs());
    const [items, setItems] = useState<AppointmentItem[]>([]);
    const [loading, setLoading] = useState(false);

    const [tooltipState, setTooltipState] = useState<{ isVisible: boolean; event: CalendarEvent | null; positionStyles: React.CSSProperties }>({
        isVisible: false,
        event: null,
        positionStyles: {},
    });
    const tooltipTimeoutRef = useRef<any>(null);
    const requestIdRef = useRef(0);

    const today = dayjs();
    const { search, status, telesale_id, staff_id, branch_id, type_appointment, date_from, date_to } = filters || {};
    // Mảng tạo mới mỗi lần render -> so sánh theo chuỗi để không tải lại vô ích
    const staffFilterKey = (filters?.staff_filter || []).join(',');
    const statusesKey = (filters?.statuses || []).join(',');

    const fetchAll = useCallback(async () => {
        const reqId = ++requestIdRef.current;
        setLoading(true);
        const staff_filter = staffFilterKey ? staffFilterKey.split(',').map(Number) : undefined;
        const statuses = statusesKey ? statusesKey.split(',').map(Number) : undefined;
        try {
            const collected: AppointmentItem[] = [];
            let page = 1;
            let total = 0;
            while (page <= MAX_PAGES) {
                const res = await getAppointments({
                    search: search || undefined,
                    status,
                    telesale_id,
                    staff_id,
                    branch_id,
                    type_appointment,
                    staff_filter,
                    statuses,
                    date_from,
                    date_to,
                    page,
                    take: PAGE_SIZE,
                });
                if (!res.status) break;
                const list = res.data?.items || [];
                collected.push(...list);
                total = res.data?.total || collected.length;
                if (!list.length || collected.length >= total) break;
                page += 1;
            }
            if (reqId === requestIdRef.current) setItems(collected);
        } finally {
            if (reqId === requestIdRef.current) setLoading(false);
        }
    }, [search, status, telesale_id, staff_id, branch_id, type_appointment, staffFilterKey, statusesKey, date_from, date_to]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // Bộ lọc "Theo ngày hẹn" của trang: giới hạn dữ liệu và nhảy tới đúng kỳ đang chọn
    const scheduledRange = useMemo(() => getScheduledRange(scheduledPeriod), [scheduledPeriod]);

    useEffect(() => {
        if (!scheduledRange) return;
        const nextView = CALENDAR_VIEW_BY_PERIOD[scheduledPeriod as string];
        if (nextView) setView(nextView);
        // Giữ nguyên hôm nay nếu hôm nay nằm trong kỳ (ví dụ lọc theo tháng/năm)
        const now = dayjs();
        const inside = !now.isBefore(scheduledRange.from) && !now.isAfter(scheduledRange.to);
        setAnchor(inside ? now : scheduledRange.from);
    }, [scheduledRange, scheduledPeriod]);

    const eventsByDate = useMemo(() => {
        const map: Record<string, CalendarEvent[]> = {};
        items.forEach(item => {
            if (scheduledRange && !isScheduledInRange(item.scheduled_at, scheduledRange)) return;
            const ev = mapAppointment(item);
            if (!ev) return;
            if (!map[ev.dateKey]) map[ev.dateKey] = [];
            map[ev.dateKey].push(ev);
        });
        Object.keys(map).forEach(k => map[k].sort((a, b) => a.minutes - b.minutes));
        return map;
    }, [items, scheduledRange]);

    const visibleCount = useMemo(() => {
        if (view === 'day') return (eventsByDate[anchor.format('YYYY-MM-DD')] || []).length;
        if (view === 'week') {
            const start = anchor.startOf('isoWeek');
            return Array.from({ length: 7 }, (_, i) => start.add(i, 'day'))
                .reduce((sum, d) => sum + (eventsByDate[d.format('YYYY-MM-DD')] || []).length, 0);
        }
        return Object.keys(eventsByDate)
            .filter(k => dayjs(k).month() === anchor.month() && dayjs(k).year() === anchor.year())
            .reduce((sum, k) => sum + eventsByDate[k].length, 0);
    }, [view, anchor, eventsByDate]);

    useEffect(() => {
        const handleOutsideClick = () => setTooltipState(prev => ({ ...prev, isVisible: false }));
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, []);

    const handleInteract = (e: React.MouseEvent, event: CalendarEvent) => {
        e.stopPropagation();
        clearTimeout(tooltipTimeoutRef.current);
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setTooltipState({
            isVisible: true,
            event,
            positionStyles: computePopoverPosition(rect),
        });
    };

    const handleHoverEnd = () => {
        tooltipTimeoutRef.current = setTimeout(() => {
            setTooltipState(prev => ({ ...prev, isVisible: false }));
        }, 200);
    };

    const handleOpenDetail = (event: CalendarEvent) => {
        navigate(detailBasePath
            ? `${detailBasePath}/${event.id}`
            : event.detailPath || `/appointment/detail/${event.id}`);
    };

    const step = (dir: number) => {
        const unit = view === 'month' ? 'month' : view === 'week' ? 'week' : 'day';
        setAnchor(prev => prev.add(dir, unit));
    };

    const title = useMemo(() => {
        if (view === 'month') return `tháng ${anchor.month() + 1} năm ${anchor.year()}`;
        if (view === 'week') {
            const start = anchor.startOf('isoWeek');
            const end = start.add(6, 'day');
            return (
                <span className="flex items-baseline gap-2">
                    Tuần {start.format('D/M')} - {end.format('D/M')}
                    <span className="text-sm sm:text-base font-normal text-gray-500">{end.year()}</span>
                </span>
            );
        }
        return (
            <span className="flex items-baseline gap-2">
                {WEEK_DAYS_FULL[anchor.isoWeekday() - 1]}, {anchor.date()} tháng {anchor.month() + 1}
                <span className="text-sm sm:text-base font-normal text-gray-500">{anchor.year()}</span>
            </span>
        );
    }, [view, anchor]);

    const viewProps: ViewProps = {
        anchor,
        eventsByDate,
        today,
        onInteract: handleInteract,
        onHoverEnd: handleHoverEnd,
    };

    return (
        <div className="bg-gray-100 dark:bg-gray-950 font-sans text-gray-800 transition-colors duration-200">
            <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #475569; }
        .animate-fade-in { animation: fadeIn 0.3s ease-in-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

            <div className="max-w-full mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden transition-colors">

                <div className="p-3 sm:p-4 flex flex-col md:flex-row justify-between items-center gap-4 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center w-full md:w-auto justify-between md:justify-start">
                        <div className="flex">
                            <button onClick={() => step(-1)} className="p-1.5 sm:p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700 rounded-l-md hover:bg-gray-50 dark:hover:bg-gray-800"><ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                            <button onClick={() => step(1)} className="p-1.5 sm:p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border-t border-b border-r border-gray-200 dark:border-gray-700 rounded-r-md hover:bg-gray-50 dark:hover:bg-gray-800"><ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                        </div>
                        <button
                            onClick={() => setAnchor(dayjs())}
                            className="ml-2 px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                            Hôm nay
                        </button>
                        <h2 className="text-base sm:text-xl font-bold text-gray-800 dark:text-white ml-3 sm:ml-4">{title}</h2>
                        {loading ? (
                            <Loader2 className="w-4 h-4 ml-3 text-gray-400 animate-spin" />
                        ) : (
                            <span className="ml-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{visibleCount} lịch hẹn</span>
                        )}
                        {scheduledRange && (
                            <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 whitespace-nowrap">
                                Lọc ngày hẹn: {scheduledRange.from.isSame(scheduledRange.to, 'day')
                                    ? scheduledRange.from.format('DD/MM/YYYY')
                                    : `${scheduledRange.from.format('DD/MM')} – ${scheduledRange.to.format('DD/MM/YYYY')}`}
                            </span>
                        )}
                    </div>

                    <div className="flex border border-gray-300 dark:border-gray-700 rounded-md overflow-hidden w-full md:w-auto">
                        {([['month', 'Tháng'], ['week', 'Tuần'], ['day', 'Ngày']] as const).map(([key, label], idx) => (
                            <button
                                key={key}
                                onClick={() => setView(key)}
                                className={`flex-1 md:flex-none px-3 sm:px-4 py-1.5 text-sm font-medium transition-colors ${idx > 0 ? 'border-l border-gray-300 dark:border-gray-700' : ''} ${view === key ? 'bg-[#3b82f6] text-white' : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-900 relative">
                    {loading && (
                        <div className="absolute inset-0 z-30 bg-white/60 dark:bg-gray-900/60 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-[#3b82f6] animate-spin" />
                        </div>
                    )}
                    {view === 'month' && <MonthView {...viewProps} />}
                    {view === 'week' && <WeekView {...viewProps} />}
                    {view === 'day' && <DayView {...viewProps} />}
                </div>
            </div>

            <AppointmentDetailPopover
                info={tooltipState.event}
                isVisible={tooltipState.isVisible}
                positionStyles={tooltipState.positionStyles}
                onMouseEnter={() => clearTimeout(tooltipTimeoutRef.current)}
                onMouseLeave={handleHoverEnd}
                onClose={() => setTooltipState(prev => ({ ...prev, isVisible: false }))}
                onOpenDetail={handleOpenDetail}
            />
        </div>
    );
}
